import type { TextPromptWithInputItem } from '@/app/types';
import { getClawTemplateTokenText } from '@/app/utils';
import type { ClawSendFile, SessionMode, YoloMessageOptions } from '../../../../types';
import {
  type DraftAttachment,
  type PendingUploadAttachment,
  partitionAttachmentsForSend,
} from './attachment-normalize';

/**
 * inputValue → 单一字符串：
 * - text 节点取 text；
 * - mention 节点取 `@<id|name>`；
 * - claw 模板节点取可发送文本；
 * - 其它节点忽略。
 * 末尾 trim，保持与历史 ChatInput 行为一致。
 */
export const stringifyInputValue = (value: TextPromptWithInputItem): string => {
  return value
    .map((item) => {
      if (item.type === 'text') return item.text;
      if (item.type === 'mention') return `@${item.mention.id || item.mention.name}`;
      if (item.type === 'claw-template-token') return getClawTemplateTokenText(item.clawTemplateToken);
      return '';
    })
    .join('')
    .trim();
};

export interface BuildClawInputPayloadArgs {
  inputValue: TextPromptWithInputItem;
  attachments: DraftAttachment[];
  /** 命中的 quick prompt 元信息：label 用作 session 名称，mode 仅用于创建新 session。 */
  quickPromptMeta?: { label?: string; mode?: SessionMode } | null;
  /** Yolo 切换按钮当前状态；quick prompt 自带 mode 时优先生效。 */
  isYoloActive: boolean;
  /** 当前的 yolo 模型选择；只有最终 sessionMode === 'yolo' 时才会写进 payload。 */
  yoloModelChoice?: 'auto' | 'q2' | 'q3';
  /** 自定义 yolo options 派生函数；测试中可注入覆盖。生产代码不需要传。 */
  resolveYoloMessageOptions?: (choice: 'auto' | 'q2' | 'q3') => YoloMessageOptions;
}

export interface ClawInputSendPayload {
  content: string;
  inputValue: TextPromptWithInputItem;
  files: ClawSendFile[];
  pendingFiles?: File[];
  pendingAttachments?: PendingUploadAttachment[];
  quickPromptLabel?: string;
  sessionMode?: SessionMode;
  yoloOptions?: YoloMessageOptions;
}

/**
 * 构造 ChatInput.onSend 的最终 payload：
 * - content 走 stringifyInputValue；
 * - files / pendingFiles / pendingAttachments 走 partitionAttachmentsForSend；
 * - sessionMode 优先取 quickPromptMeta.mode，再 fallback 到 isYoloActive；
 * - yoloOptions 仅在最终 sessionMode === 'yolo' 时由 resolveYoloMessageOptions 派生。
 */
export const buildClawInputPayload = ({
  inputValue,
  attachments,
  quickPromptMeta,
  isYoloActive,
  yoloModelChoice = 'auto',
  resolveYoloMessageOptions,
}: BuildClawInputPayloadArgs): ClawInputSendPayload => {
  const content = stringifyInputValue(inputValue);
  const { sendingFiles, pendingFiles, pendingAttachments } = partitionAttachmentsForSend(attachments);
  const sessionMode = quickPromptMeta?.mode ?? (isYoloActive ? 'yolo' : undefined);
  const yoloOptions = sessionMode === 'yolo' ? resolveYoloMessageOptions?.(yoloModelChoice) : undefined;

  return {
    content,
    inputValue,
    files: sendingFiles,
    pendingFiles: pendingFiles.length > 0 ? pendingFiles : undefined,
    pendingAttachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
    quickPromptLabel: quickPromptMeta?.label,
    sessionMode,
    yoloOptions,
  };
};
